class SideGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.panels = Array.from(this.querySelectorAll('.side-gallery__panel'));
    this.buttons = Array.from(this.querySelectorAll('.side-gallery__thumbnail-button'));
    this.originals = new Map();
    this.views = new Map();
    this.retryAttempts = 0;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.side-gallery__thumbnail-button');
      if (button) this.selectMedia(button.dataset.mediaId);
    });
    this.buttons.forEach((button, index) => {
      button.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.buttons.length) % this.buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.buttons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.buttons[next].focus({ preventScroll: true });
        this.selectMedia(this.buttons[next].dataset.mediaId);
      });
    });
    const first = this.panels.find((panel) => !panel.hidden);
    if (first) this.preparePanel(first);
  }

  preparePanel(panel) {
    if (this.originals.has(panel)) return;
    const content = panel.querySelector('.side-gallery__content');
    const template = content.querySelector('[data-side-gallery-template]');
    if (template) content.replaceChildren(template.content.cloneNode(true));
    this.originals.set(panel, content.cloneNode(true));
    this.prepareModel(panel);
  }

  selectMedia(mediaId) {
    const panel = this.panels.find((item) => item.dataset.mediaId === mediaId);
    if (!panel) return;
    this.preparePanel(panel);
    const model = panel.querySelector('product-model');
    if (!panel.hidden) {
      if (model?.dataset.galleryState === 'error') this.retryModel(panel);
      else if (model && !model.hasAttribute('loaded')) model.loadContent(false);
      return;
    }
    const activePanel = this.panels.find((item) => !item.hidden);
    const activeViewer = activePanel?.querySelector('model-viewer');
    if (activeViewer?.getCameraOrbit) {
      this.views.set(activePanel, {
        orbit: activeViewer.getCameraOrbit(),
        target: activeViewer.getCameraTarget(),
        fieldOfView: activeViewer.getFieldOfView(),
      });
    }
    window.pauseAllMedia();
    this.panels.forEach((item) => { item.hidden = item !== panel; });
    this.buttons.forEach((button) => {
      const selected = button.dataset.mediaId === mediaId;
      button.closest('.side-gallery__thumbnail').classList.toggle('side-gallery__thumbnail--selected', selected);
      if (selected) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    const button = this.buttons.find((item) => item.dataset.mediaId === mediaId);
    const strip = button?.closest('.side-gallery__thumbnails');
    if (strip) {
      const bounds = button.getBoundingClientRect();
      const viewport = strip.getBoundingClientRect();
      if (bounds.left < viewport.left) strip.scrollLeft += bounds.left - viewport.left - 2;
      if (bounds.right > viewport.right) strip.scrollLeft += bounds.right - viewport.right + 2;
    }
    if (model?.dataset.galleryState === 'error') this.retryModel(panel);
    else if (model) {
      model.loadContent(false);
      const view = this.views.get(panel);
      const viewer = model.querySelector('model-viewer');
      if (view && viewer) {
        viewer.cameraOrbit = `${view.orbit.theta}rad ${view.orbit.phi}rad ${view.orbit.radius}m`;
        viewer.cameraTarget = `${view.target.x}m ${view.target.y}m ${view.target.z}m`;
        viewer.fieldOfView = `${view.fieldOfView}deg`;
        requestAnimationFrame(() => viewer.jumpCameraToGoal());
      }
    }
  }

  prepareModel(panel) {
    const model = panel.querySelector('product-model');
    if (!model) return;
    window.ShopifyXR?.setupXRElements();
    const poster = model.querySelector('.deferred-media__poster');
    const status = document.createElement('span');
    status.className = 'side-gallery__model-status';
    status.setAttribute('role', 'status');
    poster.appendChild(status);
    model.addEventListener('gallery-model-loading', () => {
      model.dataset.galleryState = 'loading';
      model.setAttribute('aria-busy', 'true');
      poster.disabled = true;
      status.textContent = this.dataset.loadingLabel;
      clearTimeout(model.galleryTimeout);
      model.galleryTimeout = setTimeout(() => fail(), 45000);
    });
    const ready = () => {
      if (!model.isConnected) return;
      clearTimeout(model.galleryTimeout);
      model.dataset.galleryState = 'ready';
      model.removeAttribute('aria-busy');
      poster.disabled = false;
      status.textContent = '';
    };
    const fail = () => {
      clearTimeout(model.galleryTimeout);
      model.dataset.galleryState = 'error';
      model.removeAttribute('aria-busy');
      poster.disabled = false;
      status.textContent = this.dataset.errorLabel;
    };
    model.addEventListener('gallery-model-ready', ready);
    model.addEventListener('gallery-model-error', fail);
    poster.addEventListener('click', (event) => {
      if (model.dataset.galleryState !== 'error') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.retryModel(panel);
    }, true);
  }

  retryModel(panel) {
    const restoreFocus = panel.contains(document.activeElement);
    const previous = panel.querySelector('product-model');
    clearTimeout(previous?.galleryTimeout);
    previous?.modelViewerUI?.pause();
    panel.querySelector('.side-gallery__content').replaceWith(this.originals.get(panel).cloneNode(true));
    this.prepareModel(panel);
    const model = panel.querySelector('product-model');
    const templateViewer = model.querySelector('template').content.querySelector('model-viewer');
    const source = new URL(templateViewer.getAttribute('src'), document.baseURI);
    source.searchParams.set('_gallery_retry', `${Date.now()}-${++this.retryAttempts}`);
    templateViewer.setAttribute('src', source.href);
    model.loadContent(false);
    if (restoreFocus) {
      const target = this.buttons.find((button) => button.dataset.mediaId === panel.dataset.mediaId) || this;
      if (target === this) this.tabIndex = -1;
      target.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('side-gallery')) customElements.define('side-gallery', SideGallery);
